import {
    CircleLayerSpecification,
    FillLayerSpecification,
    LineLayerSpecification,
    RasterLayerSpecification,
    SymbolLayerSpecification,
} from "mapbox-gl";
import MapboxDraw, { MapboxDrawOptions } from "@mapbox/mapbox-gl-draw";
import { FeatureCollection, GeoJsonProperties, MultiPolygon } from "geojson";
import { PlotType } from "@/types/enums";
export const FIELD_GEOMETRIES_SOURCE = "field-geometries";
export const IMAGERY_SOURCE = "imagery";
export const IMAGERY_LAYER = "imagery";

export const FIELD_BOUNDARY_SOURCE = "field-boundary";
export const FIELD_FILL_LAYER: FillLayerSpecification = {
    source: FIELD_BOUNDARY_SOURCE,
    id: "field-boundary-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    paint: {
        "fill-color": "#808080",
        "fill-opacity": 0.7,
    },
};

export const FIELD_LINE_LAYER: LineLayerSpecification = {
    source: FIELD_BOUNDARY_SOURCE,
    id: "field-boundary-line",
    type: "line",
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#707070",
        "line-width": 3,
    },
};

export const BULK_FIELD_SOURCE = "bulk-field";
export const BULK_FIELD_FILL_LAYER: FillLayerSpecification = {
    source: BULK_FIELD_SOURCE,
    id: "bulk-field-fill",
    type: "fill",
    filter: ["==", ["geometry-type"], "Polygon"],
    paint: {
        "fill-color": "#ff9800",
        "fill-opacity": ["case", ["get", "selected"], 0.7, 0.3],
        "fill-outline-color": "#ff9800",
    },
};
export const BULK_FIELD_TEXT_LAYER: SymbolLayerSpecification = {
    source: BULK_FIELD_SOURCE,
    id: "bulk-field-text",
    type: "symbol",
    layout: {
        "text-field": ["get", "field_display_name"],
    },
};

export const FIELD_BOUNDARY_DRAW_OPTIONS: MapboxDrawOptions = {
    displayControlsDefault: false,
    controls: {
        trash: true,
    },
    userProperties: true,
    defaultMode: "draw_polygon",
    styles: [
        ...MapboxDraw.lib.theme,
        {
            id: "draw-fill-orange",
            type: "fill",
            filter: ["all", ["==", "$type", "Polygon"]],
            paint: {
                "fill-color": "#007cbf",
                "fill-opacity": 0.5,
            },
        },
    ],
};

export const IRRIGATION_SOURCE = "irrigation-system";
export const IRRIGATION_FILL_LAYER: FillLayerSpecification = {
    source: IRRIGATION_SOURCE,
    id: "irrigation-fill-layer",
    type: "fill",
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": "#007cbf",
        "fill-opacity": 0.6,
    },
};

export const IRRIGATION_LINE_LAYER: LineLayerSpecification = {
    source: IRRIGATION_SOURCE,
    id: "irrigation-line-layer",
    type: "line",
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#74bd43",
        "line-width": 3,
    },
};

export const IRRIGATION_CENTER_SOURCE = "irrigation-center";
export const IRRIGATION_CENTER_LAYER: CircleLayerSpecification = {
    source: IRRIGATION_CENTER_SOURCE,
    id: "irrigation-center-layer",
    type: "circle",
    layout: {
        visibility: "none",
    },
    paint: {
        "circle-radius": 8,
        "circle-color": "#74bd43",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#007cbf",
    },
};

export const END_GUN_SOURCE = "end-guns";
export const END_GUN_LAYER: LineLayerSpecification = {
    source: END_GUN_SOURCE,
    id: "end-gun-layer",
    type: "line",
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": [
            "case",
            ["==", ["get", "is_on"], true],
            "#09426A",
            "#a0a0a0",
        ],
        "line-width": 15,
        "line-opacity": 0.8,
    },
};

export const SECTOR_SOURCE = "sector";
export const SECTOR_FILL_LAYER: FillLayerSpecification = {
    id: "sector-fill",
    source: SECTOR_SOURCE,
    type: "fill",
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": "#74bd43",
        "fill-opacity": 0.3,
    },
};

export const SECTOR_LINE_LAYER: LineLayerSpecification = {
    id: "sector-line",
    source: SECTOR_SOURCE,
    type: "line",
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#74bd43",
        "line-width": 3,
    },
};
export const SECTOR_TEXT_LAYER: SymbolLayerSpecification = {
    id: "sector-text",
    source: SECTOR_SOURCE,
    type: "symbol",
    layout: {
        visibility: "none",
        "text-field": [
            "concat",
            ["get", "section_number"],
            "\n",
            ["get", "treatment"],
        ],
    },
};

export const SENTINEL_PLOTS_SOURCE = "layers-sentinel-plots";
export const SENTINEL_PLOTS_FILL_LAYER: FillLayerSpecification = {
    id: SENTINEL_PLOTS_SOURCE,
    type: "fill",
    source: SENTINEL_PLOTS_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": [
            "case",
            ["==", ["get", "type"], "Canary"],
            "#FF0000",
            "#00FF00",
        ],
        "fill-opacity": 0.8,
    },
};
export const INSIGHTS_PLOTS_SOURCE = "layers-insights-plots";
export const INSIGHTS_PLOTS_FILL_LAYER: FillLayerSpecification = {
    id: INSIGHTS_PLOTS_SOURCE,
    type: "fill",
    source: INSIGHTS_PLOTS_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": [
            "case",
            ["==", ["get", "type"], "Canary"],
            "#FF0000",
            "#00FF00",
        ],
        "fill-opacity": 0.8,
    },
};
export const INSIGHTS_PLOTS_SYMBOL_LAYER: SymbolLayerSpecification = {
    id: "layers-insights-plots-symbol",
    type: "symbol",
    source: INSIGHTS_PLOTS_SOURCE,
    layout: {
        visibility: "none",
        "text-field": [
            "concat",
            ["case", ["==", ["get", "plot_type"], 1], "Canary", "Reference"],
            "\n",
            " Pair Id: ",
            ["get", "pair_id"],
        ],
        "text-size": 10,
        "text-allow-overlap": true,
    },
};

export const APPLICATION_OPERATIONS_SOURCE = "layers-application-operation";
export const APPLICATION_OPERATION_IMAGE_LAYER: RasterLayerSpecification = {
    id: APPLICATION_OPERATIONS_SOURCE,
    type: "raster",
    source: APPLICATION_OPERATIONS_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "raster-fade-duration": 0,
    },
};

export const SUMMARY_OPERATIONS_SOURCE = "layers-summary-operation";
export const SUMMARY_OPERATION_IMAGE_LAYER: RasterLayerSpecification = {
    id: SUMMARY_OPERATIONS_SOURCE,
    type: "raster",
    source: SUMMARY_OPERATIONS_SOURCE,
    paint: {
        "raster-fade-duration": 0,
    },
};

export const IRRIGATION_SUMMARY_SOURCE = "layers-irrigation-summary-operation";
export const IRRIGATION_SUMMARY_IMAGE_LAYER: RasterLayerSpecification = {
    id: IRRIGATION_SUMMARY_SOURCE,
    type: "raster",
    source: IRRIGATION_SUMMARY_SOURCE,
    paint: {
        "raster-fade-duration": 0,
    },
};

export const HARVEST_OPERATIONS_SOURCE = "layers-harvest-operation";
export const HARVEST_OPERATION_IMAGE_LAYER: RasterLayerSpecification = {
    id: HARVEST_OPERATIONS_SOURCE,
    type: "raster",
    source: HARVEST_OPERATIONS_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "raster-fade-duration": 0,
    },
};

export const PLANTING_OPERATIONS_SOURCE = "layers-planting-operation";
export const PLANTING_OPERATION_IMAGE_LAYER: RasterLayerSpecification = {
    id: PLANTING_OPERATIONS_SOURCE,
    type: "raster",
    source: PLANTING_OPERATIONS_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "raster-fade-duration": 0,
    },
};

export const MZ_COLORS = [
    "#ADD8E6",
    "#cc709e",
    "#6529e2",
    "#3bd3d4",
    "#719a1b",
    "#002f6c",
    "#bf5700",
    "#005826",
    "#993366",
    "#4d4d4d",
    "#b31b1b",
    "#007f7f",
    "#ffbf00",
    "#003366",
    "#660066",
];

export function applyMZZoneNumberColors<P extends GeoJsonProperties>(
    zones: FeatureCollection<MultiPolygon, P>,
): FeatureCollection<
    MultiPolygon,
    P & { "fill-color": string; "display-value": number | null }
> {
    return {
        ...zones,
        features: zones.features.map((feature) => {
            return {
                ...feature,
                properties: {
                    ...feature.properties,
                    "fill-color":
                        MZ_COLORS[
                            (feature.properties?.zone_number >= 0 ? feature.properties?.zone_number : 0) %
                                MZ_COLORS.length
                        ],
                    "display-value": feature.properties?.zone_number ?? null,
                },
            };
        }),
    };
}

export const PREFERRED_MANAGEMENT_ZONE_SOURCE = "preferred-management-zone";
export const PREFERRED_MANAGEMENT_ZONE_FILL_LAYER: FillLayerSpecification = {
    id: "preferred-management-zone-fill",
    type: "fill",
    source: PREFERRED_MANAGEMENT_ZONE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": ["get", "fill-color"],
    },
};
export const PREFERRED_MANAGEMENT_ZONE_LINE_LAYER: LineLayerSpecification = {
    id: "preferred-management-zone-line",
    type: "line",
    source: PREFERRED_MANAGEMENT_ZONE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": ["get", "fill-color"],
        "line-width": 3,
    },
};

export const MANAGEMENT_ZONE_SOURCE = "layers-management-zone";
export const MANAGEMENT_ZONE_FILL_LAYER: FillLayerSpecification = {
    id: "layers-management-zone-fill",
    type: "fill",
    source: MANAGEMENT_ZONE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": ["get", "fill-color"],
        "fill-opacity": 0.8,
    },
};

export const MANAGEMENT_ZONE_LINE_LAYER: LineLayerSpecification = {
    id: "layers-management-zone-line",
    type: "line",
    source: MANAGEMENT_ZONE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": ["get", "fill-color"],
        "line-width": 3,
    },
};

export const SECTOR_SUMMARY_SOURCE = "layers-sector-summary";
export const SECTOR_SUMMARY_FILL_LAYER: FillLayerSpecification = {
    id: "layers-sector-summary-fill",
    type: "fill",
    source: SECTOR_SUMMARY_SOURCE,
    paint: {
        "fill-color": ["get", "fill-color"],
        "fill-opacity": 0.5,
    },
};
export const SECTOR_SUMMARY_LINE_LAYER: LineLayerSpecification = {
    id: "layers-sector-summary-line",
    type: "line",
    source: SECTOR_SUMMARY_SOURCE,
    paint: {
        "line-color": ["get", "fill-color"],
        "line-width": 3,
        "line-opacity": 0.8,
    },
};
export const SECTOR_SUMMARY_POINT_LAYER: SymbolLayerSpecification = {
    id: "layers-sector-summary-label",
    type: "symbol",
    source: SECTOR_SUMMARY_SOURCE,
    layout: {
        "text-field": ["concat", ["get", "mean"], " lbs N"],
        "text-font": ["Open Sans Bold"],
        "text-size": 12,
    },
};

export const RX_SOURCE = "rx";
export const RX_FILL_LAYER: FillLayerSpecification = {
    id: "rx-fill",
    type: "fill",
    source: RX_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-opacity": 0.8,
    },
};
export const RX_LINE_LAYER: LineLayerSpecification = {
    id: "rx-line",
    type: "line",
    source: RX_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#09426A",
        "line-width": 1,
    },
};
export const RX_PLOT_LABEL: SymbolLayerSpecification = {
    id: "rx-plot-label",
    type: "symbol",
    source: RX_SOURCE,
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
        visibility: "none",
    },
    paint: {
        "text-color": "#000000",
    },
};
export const PLOT_RX_SOURCE = "plot-rx";
export const PLOT_RX_FILL_LAYER: FillLayerSpecification = {
    id: "plot-rx-fill",
    type: "fill",
    source: PLOT_RX_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-opacity": 0.8,
    },
};
export const PLOT_RX_LINE_LAYER: LineLayerSpecification = {
    id: "plot-rx-line",
    type: "line",
    source: PLOT_RX_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#09426A",
        "line-width": 1,
    },
};
export const PLOT_RX_PLOT_LABEL: SymbolLayerSpecification = {
    id: "plot-rx-plot-label",
    type: "symbol",
    source: PLOT_RX_SOURCE,
    layout: {
        "text-field": ["get", "pair_id"],
        "text-size": 12,
        visibility: "none",
    },
    paint: {
        "text-color": "#000000",
    },
};
export const RX_HIGHLIGHT_SOURCE = "rx-highlight";
export const RX_HIGHLIGHT_FILL_LAYER: FillLayerSpecification = {
    id: "rx-highlight-fill",
    type: "fill",
    source: RX_HIGHLIGHT_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-color": "#FFFFFF",
        "fill-opacity": 0.25,
    },
};
export const RX_HIGHLIGHT_LINE_LAYER: LineLayerSpecification = {
    id: "rx-highlight-line",
    type: "line",
    source: RX_HIGHLIGHT_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "#FFFFFF",
        "line-width": 3,
    },
};
export const PLOT_HIGHLIGHT_SOURCE = "plot-highlight";
export const PLOT_HIGHLIGHT_FILL_LAYER: FillLayerSpecification = {
    id: "plot-highlight-fill",
    type: "fill",
    source: PLOT_HIGHLIGHT_SOURCE,
    layout: {
        visibility: "visible",
    },
    paint: {
        "fill-color": [
            "case",
            ["==", ["get", "plot_type"], PlotType.Canary],
            "#FF0000",
            "#00FF00",
        ],
        "fill-opacity": 0.5,
    },
};

export const PLOT_HIGHLIGHT_LINE_LAYER: LineLayerSpecification = {
    id: "plot-highlight-line",
    type: "line",
    source: PLOT_HIGHLIGHT_SOURCE,
    layout: {
        visibility: "visible",
    },
    paint: {
        "line-color": "#FFFFFF",
        "line-width": 2,
    },
};
export const AB_LINE_SOURCE = "ab-line";
export const AB_LINE_LAYER: LineLayerSpecification = {
    id: "ab-line",
    type: "line",
    source: AB_LINE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "white",
        "line-width": 2,
        "line-dasharray": [2, 1],
    },
};
export const AB_POINTS_LAYER: CircleLayerSpecification = {
    id: "ab-points",
    type: "circle",
    source: AB_LINE_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "circle-radius": 6,
        "circle-color": "white",
    },
};
export const PLOT_GRID_SOURCE = "plot-grid";
export const PLOT_GRID_LAYER: LineLayerSpecification = {
    id: "plot-grid-line",
    type: "line",
    source: PLOT_GRID_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "line-color": "white",
        "line-width": 1,
        "line-dasharray": [2, 1],
    },
};

export const SI_SOURCE = "si-map-source";
export const SI_FILL_LAYER: FillLayerSpecification = {
    id: "si-fill",
    type: "fill",
    source: SI_SOURCE,
    layout: {
        visibility: "none",
    },
    paint: {
        "fill-opacity": 0.4,
        "fill-color": ["get", "color"],
        "fill-outline-color": "rgba(0, 0, 0, 0)",
    },
};

export const WATER_SOURCE = "water";
export const WATER_LAYER = "water";
